import { Helmet } from 'react-helmet-async';
import { Layout } from '@/components/layout/Layout';
import { useContactData } from '@/hooks/useContactData';
import { useNavigationData } from '@/hooks/useNavigationData';
import { useLocationData } from '@/hooks/useLocationData';

export function Location() {
  const { contactInfo } = useContactData();
  const { navigationItems } = useNavigationData();
  const { locationData } = useLocationData();

  return (
    <>
      <Helmet>
        <title>Location - Wayland Rod & Gun Club</title>
        <meta name="description" content="Find the Wayland Rod & Gun Club location, contact information, and facility details. Located at 4 Meadow View Road, Wayland, MA 01778." />
      </Helmet>

      <Layout contactInfo={contactInfo} navigationItems={navigationItems}>
        <div className="p-6 space-y-8">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl font-cursive text-primary-700 mb-4">
              {locationData.clubName} Location
            </h1>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto">
              {locationData.description}
            </p>
          </div>

          <hr className="border-gray-400" />

          {/* Contact Information */}
          <div className="bg-green-50 p-6 rounded-lg shadow-md">
            <h2 className="text-2xl font-bold text-green-800 mb-4">Contact Information</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Mailing Address</h3>
                <div className="text-gray-700 space-y-1">
                  <p className="font-semibold">{locationData.clubName}</p>
                  <p>{locationData.address.street}</p>
                  <p>{locationData.address.city}, {locationData.address.state} {locationData.address.zipCode}</p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Phone</h3>
                  <p className="text-gray-700 text-lg">
                    <a href={`tel:${locationData.phone}`} className="hover:text-green-600 transition-colors">
                      {locationData.phone}
                    </a>
                  </p>
                  <p className="text-sm text-gray-600 mt-2">{locationData.contactNote}</p>
                </div>
                
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Email</h3>
                  <div className="space-y-1 text-gray-700">
                    <div>
                      <span className="font-medium">Information: </span>
                      <a href={`mailto:${locationData.email.information}`} className="hover:text-green-600 transition-colors">
                        {locationData.email.information}
                      </a>
                    </div>
                    <div>
                      <span className="font-medium">Webmaster: </span>
                      <a href={`mailto:${locationData.email.webmaster}`} className="hover:text-green-600 transition-colors">
                        {locationData.email.webmaster}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Club Details */}
          <div className="bg-white p-6 rounded-lg shadow-md border">
            <h2 className="text-2xl font-bold text-green-800 mb-4">Club Details</h2>
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div className="p-4 bg-green-50 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Established</h3>
                <p className="text-2xl font-bold text-green-600">{locationData.established}</p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Property Size</h3>
                <p className="text-2xl font-bold text-green-600">{locationData.acreage} acres</p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Location</h3>
                <p className="text-sm font-medium text-gray-700">SW Corner of Wayland</p>
              </div>
            </div>
          </div>

          {/* Facilities */}
          <div className="bg-white p-6 rounded-lg shadow-md border">
            <h2 className="text-2xl font-bold text-green-800 mb-4">Our Facilities</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {locationData.facilities.map((facility, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-lg border-l-4 border-green-500">
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">{facility.name}</h3>
                  <p className="text-gray-700 text-sm">{facility.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Directions */}
          <div className="bg-blue-50 p-6 rounded-lg shadow-md">
            <h2 className="text-2xl font-bold text-blue-800 mb-4">Directions</h2>
            <p className="text-gray-700 mb-4">{locationData.directions}</p>
            <div className="bg-white p-4 rounded border border-gray-300">
              <h3 className="font-semibold text-gray-800 mb-2">Full Address for GPS:</h3>
              <p className="text-gray-700 font-mono">
                {locationData.address.street}, {locationData.address.city}, {locationData.address.state} {locationData.address.zipCode}
              </p>
            </div>
          </div>

          {/* Detailed Directions */}
          <div className="grid md:grid-cols-2 gap-8">
            {/* Club House Directions */}
            <div className="bg-green-50 p-6 rounded-lg shadow-md border-l-4 border-green-500">
              <h2 className="text-2xl font-bold text-green-800 mb-4">Club House Directions</h2>
              <div className="mb-4">
                <a 
                  href={locationData.detailedDirections.clubHouse.mapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded transition-colors"
                >
                  📍 View Club House on Map
                </a>
              </div>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-green-800 mb-3">From Route 128 - I95</h3>
                  <ol className="space-y-2 text-sm text-gray-700">
                    {locationData.detailedDirections.clubHouse.fromRoute128.map((step, index) => (
                      <li key={index} className="flex">
                        <span className="font-bold text-green-600 mr-2 min-w-[20px]">{index + 1}.</span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-green-800 mb-3">From Mass Pike</h3>
                  <ol className="space-y-2 text-sm text-gray-700">
                    {locationData.detailedDirections.clubHouse.fromMassPike.map((step, index) => (
                      <li key={index} className="flex">
                        <span className="font-bold text-green-600 mr-2 min-w-[20px]">{index + 1}.</span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            </div>

            {/* Outdoor Range Directions */}
            <div className="bg-orange-50 p-6 rounded-lg shadow-md border-l-4 border-orange-500">
              <h2 className="text-2xl font-bold text-orange-800 mb-4">Outdoor Range Directions</h2>
              <div className="mb-4">
                <a 
                  href={locationData.detailedDirections.outdoorRange.mapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-orange-600 hover:bg-orange-700 text-white font-semibold py-2 px-4 rounded transition-colors"
                >
                  📍 View Outdoor Range on Map
                </a>
              </div>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-orange-800 mb-3">From Route 128 - I95</h3>
                  <ol className="space-y-2 text-sm text-gray-700">
                    {locationData.detailedDirections.outdoorRange.fromRoute128.map((step, index) => (
                      <li key={index} className="flex">
                        <span className="font-bold text-orange-600 mr-2 min-w-[20px]">{index + 1}.</span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-orange-800 mb-3">From Mass Pike</h3>
                  <ol className="space-y-2 text-sm text-gray-700">
                    {locationData.detailedDirections.outdoorRange.fromMassPike.map((step, index) => (
                      <li key={index} className="flex">
                        <span className="font-bold text-orange-600 mr-2 min-w-[20px]">{index + 1}.</span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            </div>
          </div>

        </div>
      </Layout>
    </>
  );
}